import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { EventSchema } from 'types';
import {
  EVENT_DESCRIPTION_MAX_LENGTH,
  EVENT_POSTER_SIZE_LIMIT,
  EVENT_POSTER_SUPPORTED_FORMATS,
  EVENT_TITLE_MAX_LENGTH
} from 'utils';
import {
  hasMembershipPriceSelector,
  isMembersOnlyAvailableSelector,
  isRecordingAvailableSelector
} from 'store/selectors';
import { api, LoggerService } from 'services';
import { useRouter, useStore, useToast } from 'hooks';
import { ImageUpload } from 'containers';
import { Input, Loader, Switch, TextArea } from 'components';
import { RecordingIcon, UserGroupIcon } from 'components/Icon';

import styles from './EditScheduledEventPanel.module.scss';

export type EditScheduledEventPanelProps = {
  event: EventSchema;
  onSubmitted?(updatedEvent: EventSchema): void;
};

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .max(EVENT_TITLE_MAX_LENGTH, `must be less than ${EVENT_TITLE_MAX_LENGTH} characters`)
    .required('Event topic is required'),
  isMembersOnly: yup.boolean().required(),
  isRecordingEnabled: yup.boolean().required(),
  description: yup
    .string()
    .max(EVENT_DESCRIPTION_MAX_LENGTH, `must be less than ${EVENT_DESCRIPTION_MAX_LENGTH} characters`),
  poster: yup
    .mixed()
    .test('fileSize', 'The file is too large', (file: File) => !file || file.size <= EVENT_POSTER_SIZE_LIMIT)
    .test(
      'fileFormat',
      'File format is not supported',
      (file: File) => !file || EVENT_POSTER_SUPPORTED_FORMATS.map(ext => `image/${ext}`).includes(file?.type)
    )
});

const EditScheduledEventPanel = ({ event, onSubmitted }: EditScheduledEventPanelProps): JSX.Element => {
  const router = useRouter();
  const toast = useToast();
  const { isMembersOnlyAvailable, isRecordingAvailable, hasMembershipPrice } = useStore(state => ({
    isMembersOnlyAvailable: isMembersOnlyAvailableSelector(state),
    isRecordingAvailable: isRecordingAvailableSelector(state),
    hasMembershipPrice: hasMembershipPriceSelector(state)
  }));
  const isMembersOnlyToggleActive = isMembersOnlyAvailable && hasMembershipPrice;
  const isRecordingToggleActive = isRecordingAvailable && hasMembershipPrice;

  const form = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: event.name,
      isMembersOnly: !!event.memberOnly,
      isRecordingEnabled: !!event.recordingEnabled,
      description: event.description,
      poster: null
    }
  });

  // reset form default values when event props changes
  useEffect(() => {
    form.reset({
      name: event.name,
      isMembersOnly: !!event.memberOnly,
      isRecordingEnabled: !!event.recordingEnabled,
      description: event.description
    });
  }, [JSON.stringify(event)]);

  const onEnablePremiumForMembersOnly = () => {
    if (!isMembersOnlyAvailable) {
      router.push('/pricing', { membersOnly: true });

      return;
    }

    if (!hasMembershipPrice) {
      router.push('/get-paid');
    }
  };

  const onEnablePremiumForRecording = () => {
    if (!isRecordingAvailable) {
      router.push('/pricing', { recording: true });

      return;
    }

    if (!hasMembershipPrice) {
      router.push('/get-paid');
    }
  };

  return (
    <div className={styles.container}>
      {form.formState.isSubmitting && <Loader />}

      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(async validValues => {
            try {
              const { data: updatedEvent } = await api.requests.ltv.updateScheduledEvent({
                code: event.code,
                name: validValues.name,
                memberOnly: validValues.isMembersOnly,
                enableRecording: validValues.isRecordingEnabled,
                description: validValues.description,
                poster: validValues.poster || undefined
              });

              toast.success('Event details updated', { id: `UPDATE_SCHEDULED_EVENT:${event.code}` });

              onSubmitted?.(updatedEvent);
            } catch (error) {
              LoggerService.log('SUBMIT_EDIT_SCHEDULED_EVENT', error);

              if (error instanceof Error) {
                toast.error(error.message, { id: `UPDATE_SCHEDULED_EVENT:${event.code}` });
              }
            }
          })}
        >
          <Controller
            name="poster"
            control={form.control}
            render={({ field: { value, onChange } }) => (
              <ImageUpload
                actions={['update']}
                defaultValue={event.poster}
                maxSize={EVENT_POSTER_SIZE_LIMIT}
                className={styles.poster}
                invalid={!!form.formState.errors.poster}
                feedback={form.formState.errors.poster?.message}
                value={value}
                onChange={onChange}
              />
            )}
          />

          <Controller
            name="name"
            control={form.control}
            render={({ field }) => (
              <Input
                className={styles.name}
                invalid={!!form.formState.errors.name}
                placeholder="Add a Topic"
                maxLength={EVENT_TITLE_MAX_LENGTH}
                feedback={form.formState.errors.name?.message}
                {...field}
              />
            )}
          />

          <ul className={styles.toggles}>
            <li>
              <Controller
                control={form.control}
                name="isMembersOnly"
                render={({ field: { value, onChange } }) => (
                  <Switch
                    className={styles.toggle}
                    icon={<UserGroupIcon />}
                    title="Members Only"
                    checked={value}
                    badge={!isMembersOnlyAvailable ? 'Premium' : ''}
                    disabled={!isMembersOnlyToggleActive}
                    onChange={onChange}
                    onClick={onEnablePremiumForMembersOnly}
                  />
                )}
              />
            </li>
            <li>
              <Controller
                control={form.control}
                name="isRecordingEnabled"
                render={({ field: { value, onChange } }) => (
                  <Switch
                    className={styles.toggle}
                    icon={<RecordingIcon />}
                    title="Recording"
                    checked={value}
                    badge={!isRecordingAvailable ? 'Premium' : ''}
                    disabled={!isRecordingToggleActive}
                    onChange={onChange}
                    onClick={onEnablePremiumForRecording}
                  />
                )}
              />
            </li>
          </ul>

          <Controller
            name="description"
            control={form.control}
            render={({ field }) => (
              <TextArea
                className={styles.description}
                maxLength={EVENT_DESCRIPTION_MAX_LENGTH}
                placeholder="Description"
                {...field}
              />
            )}
          />

          <button type="submit">
            <span>Save changes</span>
          </button>
        </form>
      </FormProvider>
    </div>
  );
};

export default EditScheduledEventPanel;
